<template>
  <div class="space-y-6">
    <!-- Back Button -->
    <button
      @click="$emit('back')"
      class="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition -m-2 p-2"
    >
      <svg
        class="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
      <span class="text-lg sm:text-xl font-medium">Back</span>
    </button>

    <!-- Check-in For Card -->
    <div class="bg-white rounded-xl shadow-md p-4 sm:p-6">
      <p
        class="text-sm sm:text-base font-semibold text-gray-500 uppercase tracking-wide mb-2"
      >
        Check-in for
      </p>
      <div class="flex">
        <!-- Avatar -->
        <img
          v-if="wrestler.avatar_thumbnail"
          :src="wrestler.avatar_thumbnail"
          class="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover mr-3 sm:mr-4 flex-shrink-0"
          alt=""
        />
        <span
          v-else
          class="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 mr-3 sm:mr-4 flex items-center justify-center flex-shrink-0"
        >
          <svg
            class="w-6 h-6 sm:w-8 sm:h-8 text-gray-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clip-rule="evenodd"
            />
          </svg>
        </span>

        <!-- Name & Info -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-3 flex-wrap">
            <span class="font-semibold text-gray-900 text-xl sm:text-2xl break-words min-w-0">
              {{ wrestler.display_name || wrestler.full_name }}
            </span>
            <!-- Roster Tags -->
            <div
              v-if="rosterTags.length > 0"
              class="flex flex-wrap gap-1 items-center"
            >
              <span
                v-for="tag in rosterTags"
                :key="tag.abbreviation"
                class="px-2 py-0.5 text-sm sm:text-xs font-medium rounded"
                :style="{ backgroundColor: tag.color, color: tag.text_color }"
              >
                {{ tag.abbreviation
                }}{{ tag.count > 1 ? ` x${tag.count}` : "" }}
              </span>
              <!-- Expand button to show roster names -->
              <button
                @click.stop="rostersExpanded = !rostersExpanded"
                class="px-2 py-0.5 text-sm sm:text-xs font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300 transition"
              >
                <svg
                  v-if="!rostersExpanded"
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <svg
                  v-else
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
          <!-- Expanded Roster Names -->
          <div
            v-if="
              rostersExpanded && wrestler.rosters && wrestler.rosters.length > 0
            "
            class="text-base sm:text-sm text-gray-600 mt-1 break-words"
          >
            {{ rosterNames }}
          </div>
          <span
            v-if="wrestler.age || wrestler.weight_class"
            class="block text-base sm:text-sm text-gray-500 mt-1"
          >
            <template v-if="wrestler.weight_class"
              >{{ wrestler.weight_class }} lbs</template
            >
            <template v-if="wrestler.age && wrestler.weight_class">
              ·
            </template>
            <template v-if="wrestler.age"
              >{{ wrestler.age }} years old</template
            >
          </span>
          <!-- Standalone weigh-in (no check-in, e.g. after practice) is a secondary action, so it's
               a quiet link beside the last weight rather than a button that competes with
               the class check-in buttons below -->
          <span
            v-if="weightTrackingEnabled"
            class="flex flex-wrap items-center gap-x-2 text-base sm:text-sm text-gray-500 mt-1"
          >
            <template v-if="wrestler.latest_weight">
              <span>Last logged: {{ wrestler.latest_weight }} {{ wrestler.latest_weight_units }}</span>
              <span aria-hidden="true">·</span>
            </template>
            <button
              type="button"
              class="py-1 -my-1 font-semibold underline underline-offset-2"
              style="color: var(--team-primary);"
              @click="$emit('log-weight')"
            >
              Log weight
            </button>
          </span>
        </div>
      </div>
    </div>

    <!-- Today's Classes Card -->
    <div class="bg-white rounded-xl shadow-md p-4 sm:p-6">
      <div class="border-b border-gray-200 pb-4 mb-4 sm:mb-6">
        <h2 class="text-xl sm:text-2xl font-semibold text-gray-900">
          Your classes for {{ formattedToday }}
        </h2>
      </div>

      <div class="space-y-4">
        <!-- Events List -->
        <!-- Eligible Event Row (clickable) -->
        <button
          v-for="event in eligibleEvents"
          :key="event.id"
          @click="handleEventClick(event)"
          class="event-row flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-8 w-full text-left p-3 -mx-3 rounded-lg transition group hover:bg-gray-50"
        >
          <!-- Event Info -->
          <span class="flex-1 min-w-0">
            <span
              class="flex items-start md:items-center gap-2 md:gap-4 text-base sm:text-lg md:text-xl font-medium text-gray-700"
            >
              <span class="flex items-center gap-2 md:gap-4 flex-shrink-0">
                <span class="min-w-[4.5rem] md:min-w-[5rem] tabular-nums">{{ formatEventTime(event) }}</span>
                <span
                  v-if="event.color"
                  class="w-3 h-3 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: event.color }"
                ></span>
              </span>
              <span class="break-words min-w-0">{{ event.name }}</span>
            </span>
          </span>

          <!-- Check-in Button -->
          <span
            class="flex items-center justify-center gap-1 px-3 md:px-4 py-2 rounded-lg font-semibold whitespace-nowrap flex-shrink-0 transition w-full md:w-auto"
            style="
              background-color: var(--team-primary);
              color: var(--team-text);
            "
          >
            <svg class="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M11 7L9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-8v2h8v14z"
              />
            </svg>
            <span>Check-in</span>
          </span>
        </button>

        <!-- Empty State for Eligible Events -->
        <p v-if="eligibleEvents.length === 0" class="text-gray-400 text-base sm:text-lg py-2">
          You have no classes available for check-in
        </p>

        <!-- Other Classes Section -->
        <template v-if="ineligibleEvents.length > 0">
          <div class="border-t border-gray-200 pt-4 sm:pt-6 mt-4 sm:mt-6">
            <p class="text-base sm:text-lg font-semibold text-gray-500 mb-4">
              Other classes today
            </p>
          </div>
        </template>

        <!-- Ineligible Event Row (shows more info) -->
        <button
          v-for="event in ineligibleEvents"
          :key="'ineligible-' + event.id"
          @click="showIneligibleInfo(event)"
          class="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-8 w-full text-left p-3 -mx-3 rounded-lg transition hover:bg-gray-50"
        >
          <!-- Event Info -->
          <span class="flex-1 min-w-0">
            <span
              class="flex items-start md:items-center gap-2 md:gap-4 text-base sm:text-lg md:text-xl font-medium text-gray-400"
            >
              <span class="flex items-center gap-2 md:gap-4 flex-shrink-0">
                <span class="min-w-[4.5rem] md:min-w-[5rem] tabular-nums">{{ formatEventTime(event) }}</span>
                <span
                  v-if="event.color"
                  class="w-3 h-3 rounded-full flex-shrink-0 opacity-50"
                  :style="{ backgroundColor: event.color }"
                ></span>
              </span>
              <span class="break-words min-w-0">{{ event.name }}</span>
            </span>
          </span>

          <!-- More Info Button -->
          <span
            class="flex items-center justify-center px-3 md:px-4 py-2 rounded-lg font-semibold whitespace-nowrap flex-shrink-0 border-2 w-full md:w-auto border-gray-300 text-gray-400"
          >
            Check in
          </span>
        </button>

        <!-- General Check-in Option -->
        <button
          v-if="showGeneralCheckIn"
          @click="handleEventClick({ id: -1, name: 'General Check-in' })"
          class="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-8 w-full text-left p-3 -mx-3 rounded-lg transition hover:bg-gray-50 pt-4 border-t border-gray-200 mt-4"
        >
          <span class="flex-1 min-w-0">
            <span
              class="flex items-center gap-4 text-base sm:text-lg md:text-xl font-medium text-gray-700"
            >
              <span>General Check-in</span>
            </span>
            <span class="block text-sm text-gray-500 mt-1"
              >Track facility access without a specific event</span
            >
          </span>
          <span
            class="flex items-center justify-center gap-1 px-3 md:px-4 py-2 rounded-lg font-semibold whitespace-nowrap flex-shrink-0 transition w-full md:w-auto"
            style="
              background-color: var(--team-primary);
              color: var(--team-text);
            "
          >
            <svg class="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M11 7L9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-8v2h8v14z"
              />
            </svg>
            <span>Check-in</span>
          </span>
        </button>

        <!-- Empty State -->
        <div
          v-if="sortedEvents.length === 0 && !showGeneralCheckIn"
          class="text-center py-12"
        >
          <svg
            class="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p class="text-gray-500 mb-2">
            No events available for check-in today
          </p>
          <p class="text-sm text-gray-400">
            Contact your coach if you believe this is an error
          </p>
        </div>
      </div>
    </div>

    <!-- Ineligible (Roster) Modal -->
    <div
      v-if="ineligibleModalEvent"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 !m-0 overflow-y-auto"
      @click.self="ineligibleModalEvent = null"
    >
      <div
        class="bg-white rounded-xl shadow-xl max-w-md w-full p-4 sm:p-6 flex flex-col items-center gap-4 sm:gap-6 my-auto"
      >
        <!-- Person Alert Icon -->
        <svg
          class="w-16 h-16"
          viewBox="0 0 24 24"
          fill="none"
          style="color: var(--team-primary)"
        >
          <path
            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
            fill="currentColor"
          />
          <circle cx="19" cy="3" r="3" fill="currentColor" />
          <path
            d="M19 2v2M19 2v2"
            stroke="white"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>

        <!-- Text Content -->
        <div class="flex flex-col gap-4 text-center">
          <h3 class="text-xl font-bold text-gray-900">Can't check in</h3>
          <p class="text-base font-medium text-gray-600 leading-6">
            You are not on a roster for this class. Please select another class
            or ask a coach for assistance.
          </p>
        </div>

        <!-- Registration QR Code -->
        <div
          v-if="registrationQrCodeUrl"
          class="flex flex-col items-center gap-3 border-t border-gray-200 pt-4 sm:pt-6 w-full"
        >
          <p class="text-base sm:text-sm font-medium text-gray-500 text-center text-balance">
            Need to register? Scan this code on your phone to sign up:
          </p>
          <img
            :src="registrationQrCodeUrl"
            alt="Registration QR Code"
            class="w-32 h-32 sm:w-40 sm:h-40 border border-gray-200 rounded-lg"
          />
          <!-- Let next person use kiosk -->
          <button
            @click="$emit('back')"
            class="w-full mt-2 px-4 py-3 rounded-lg font-medium text-base border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition"
          >
            Done, let someone else check in
          </button>
        </div>

        <hr class="w-full border-gray-200" />

        <!-- Select Another Class Button -->
        <button
          @click="ineligibleModalEvent = null"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base"
          style="background-color: var(--team-primary); color: var(--team-text)"
        >
          Select a different class
        </button>

        <!-- Admin override button -->
        <button
          @click="handleForceCheckIn(ineligibleModalEvent, 'roster')"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base border-2 transition hover:opacity-80"
          style="border-color: var(--team-primary); color: var(--team-primary)"
        >
          <span>Admin Override</span>
          <span class="block text-sm font-normal text-gray-500 mt-1">Get a coach or admin to check you in</span>
        </button>
      </div>
    </div>

    <!-- USAW Membership Required Modal -->
    <div
      v-if="usawModalEvent"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 !m-0 overflow-y-auto"
      @click.self="usawModalEvent = null"
    >
      <div
        class="bg-white rounded-xl shadow-xl max-w-md w-full p-4 sm:p-6 flex flex-col items-center gap-4 sm:gap-6 my-auto"
      >
        <!-- Problem Icon -->
        <svg
          class="w-16 h-16"
          viewBox="0 0 24 24"
          fill="none"
          style="color: var(--team-primary)"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            fill="currentColor"
          />
        </svg>

        <!-- Text Content -->
        <div class="flex flex-col gap-4 text-center">
          <h3 class="text-xl font-bold text-gray-900">
            USAW Membership Required
          </h3>
          <p class="text-base font-medium text-gray-600 leading-6">
            This class requires an active USA Wrestling (USAW) membership. Your USAW card is expired, not purchased, or has not been
            entered into WrestlingIQ.
          </p>
        </div>

        <!-- Select Another Class Button -->
        <button
          @click="usawModalEvent = null"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base"
          style="background-color: var(--team-primary); color: var(--team-text)"
        >
          Select a different class
        </button>

        <!-- Admin override button -->
        <button
          @click="handleForceCheckIn(usawModalEvent, 'usaw')"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base border-2 transition hover:opacity-80"
          style="border-color: var(--team-primary); color: var(--team-primary)"
        >
          <span>Admin Override</span>
          <span class="block text-sm font-normal text-gray-500 mt-1">Get a coach or admin to check you in</span>
        </button>
      </div>
    </div>

    <!-- AAU Membership Required Modal -->
    <div
      v-if="aauModalEvent"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 !m-0 overflow-y-auto"
      @click.self="aauModalEvent = null"
    >
      <div
        class="bg-white rounded-xl shadow-xl max-w-md w-full p-4 sm:p-6 flex flex-col items-center gap-4 sm:gap-6 my-auto"
      >
        <!-- Problem Icon -->
        <svg
          class="w-16 h-16"
          viewBox="0 0 24 24"
          fill="none"
          style="color: var(--team-primary)"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            fill="currentColor"
          />
        </svg>

        <!-- Text Content -->
        <div class="flex flex-col gap-4 text-center">
          <h3 class="text-xl font-bold text-gray-900">
            AAU Membership Required
          </h3>
          <p class="text-base font-medium text-gray-600 leading-6">
            This class requires an active AAU membership. It looks like your AAU
            card is either expired, hasn't been purchased, or hasn't been added
            to WrestlingIQ yet. Please update your membership so you can join
            this class.
          </p>
        </div>

        <!-- Select Another Class Button -->
        <button
          @click="aauModalEvent = null"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base"
          style="background-color: var(--team-primary); color: var(--team-text)"
        >
          Select a different class
        </button>

        <!-- Admin override button -->
        <button
          @click="handleForceCheckIn(aauModalEvent, 'aau')"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base border-2 transition hover:opacity-80"
          style="border-color: var(--team-primary); color: var(--team-primary)"
        >
          <span>Admin Override</span>
          <span class="block text-sm font-normal text-gray-500 mt-1">Get a coach or admin to check you in</span>
        </button>
      </div>
    </div>

    <!-- Subscription Paused Modal -->
    <div
      v-if="subscriptionPausedModalEvent"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 !m-0 overflow-y-auto"
      @click.self="subscriptionPausedModalEvent = null"
    >
      <div
        class="bg-white rounded-xl shadow-xl max-w-md w-full p-4 sm:p-6 flex flex-col items-center gap-4 sm:gap-6 my-auto"
      >
        <!-- Pause/Payment Icon -->
        <svg
          class="w-16 h-16"
          viewBox="0 0 24 24"
          fill="none"
          style="color: var(--team-primary)"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"
            fill="currentColor"
          />
        </svg>

        <!-- Text Content -->
        <div class="flex flex-col gap-4 text-center">
          <h3 class="text-xl font-bold text-gray-900">
            Subscription Paused
          </h3>
          <p class="text-base font-medium text-gray-600 leading-6">
            {{ wrestler.display_name || wrestler.full_name }}'s subscription payments are currently paused.
            Please ask a coach or admin to resume payment collection before checking in.
          </p>
        </div>

        <!-- Close Button -->
        <button
          @click="subscriptionPausedModalEvent = null"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base"
          style="background-color: var(--team-primary); color: var(--team-text)"
        >
          OK
        </button>

        <!-- Admin override button -->
        <button
          @click="handleForceCheckIn(subscriptionPausedModalEvent, 'subscription_paused')"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base border-2 transition hover:opacity-80"
          style="border-color: var(--team-primary); color: var(--team-primary)"
        >
          <span>Admin Override</span>
          <span class="block text-sm font-normal text-gray-500 mt-1">Get a coach or admin to check you in</span>
        </button>
      </div>
    </div>

    <!-- Subscription Overdue Modal -->
    <div
      v-if="subscriptionOverdueModalEvent"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 !m-0 overflow-y-auto"
      @click.self="subscriptionOverdueModalEvent = null"
    >
      <div
        class="bg-white rounded-xl shadow-xl max-w-md w-full p-4 sm:p-6 flex flex-col items-center gap-4 sm:gap-6 my-auto"
      >
        <!-- Warning/Payment Icon -->
        <svg
          class="w-16 h-16"
          viewBox="0 0 24 24"
          fill="none"
          style="color: var(--team-primary)"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            fill="currentColor"
          />
        </svg>

        <!-- Text Content -->
        <div class="flex flex-col gap-4 text-center">
          <h3 class="text-xl font-bold text-gray-900">
            Subscription Overdue
          </h3>
          <p class="text-base font-medium text-gray-600 leading-6">
            {{ wrestler.display_name || wrestler.full_name }}'s subscription payment is overdue.
            Please update your payment method or contact a coach for assistance.
          </p>
        </div>

        <!-- Close Button -->
        <button
          @click="subscriptionOverdueModalEvent = null"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base"
          style="background-color: var(--team-primary); color: var(--team-text)"
        >
          OK
        </button>

        <!-- Admin override button -->
        <button
          @click="handleForceCheckIn(subscriptionOverdueModalEvent, 'subscription_overdue')"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base border-2 transition hover:opacity-80"
          style="border-color: var(--team-primary); color: var(--team-primary)"
        >
          <span>Admin Override</span>
          <span class="block text-sm font-normal text-gray-500 mt-1">Get a coach or admin to check you in</span>
        </button>
      </div>
    </div>

    <!-- Weekly Limit Reached Modal (class-pass-limited subscription, 0 passes available) -->
    <div
      v-if="weeklyLimitModalEvent"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 !m-0 overflow-y-auto"
      @click.self="weeklyLimitModalEvent = null"
    >
      <div
        class="bg-white rounded-xl shadow-xl max-w-md w-full p-4 sm:p-6 flex flex-col items-center gap-4 sm:gap-6 my-auto"
      >
        <svg
          class="w-16 h-16"
          viewBox="0 0 24 24"
          fill="none"
          style="color: var(--team-primary)"
        >
          <path
            d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 11h-2V7h2v6zm-1 4a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z"
            fill="currentColor"
          />
        </svg>

        <div class="flex flex-col gap-4 text-center">
          <h3 class="text-xl font-bold text-gray-900">
            Weekly limit reached
          </h3>
          <p class="text-base font-medium text-gray-600 leading-6">
            {{ wrestler.display_name || wrestler.full_name }} is on a limited attendance plan and has reached their max practices for this week.
          </p>
        </div>

        <button
          @click="weeklyLimitModalEvent = null"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base"
          style="background-color: var(--team-primary); color: var(--team-text)"
        >
          OK
        </button>

        <button
          @click="handleForceCheckIn(weeklyLimitModalEvent, 'weekly_limit')"
          class="w-full px-6 py-3 rounded-lg font-semibold text-base border-2 transition hover:opacity-80"
          style="border-color: var(--team-primary); color: var(--team-primary)"
        >
          <span>Admin Override</span>
          <span class="block text-sm font-normal text-gray-500 mt-1">Get a coach or admin to check you in</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KioskEventSelector',
  props: {
    wrestler: {
      type: Object,
      required: true,
    },
    events: {
      type: Array,
      required: true,
    },
    requiresUsaw: {
      type: Boolean,
      default: false,
    },
    requiresAau: {
      type: Boolean,
      default: false,
    },
    allowGeneralCheckIn: {
      type: Boolean,
      default: false,
    },
    registrationQrCodeUrl: {
      type: String,
      default: null,
    },
    teamName: {
      type: String,
      default: '',
    },
    checkInLogic: {
      type: Object,
      default: null,
    },
    warpSpeed: {
      type: Boolean,
      default: false,
    },
    // Team has weight tracking on AND this wrestler is not opted out via their rosters
    weightTrackingEnabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select', 'back', 'force-checkin', 'log-weight', 'modal-state-changed', 'error-modal-opened'],
  data() {
    return {
      ineligibleModalEvent: null,
      aauModalEvent: null,
      usawModalEvent: null,
      subscriptionPausedModalEvent: null,
      subscriptionOverdueModalEvent: null,
      weeklyLimitModalEvent: null,
      rostersExpanded: false,
    };
  },
  computed: {
    // Track if any eligibility modal is currently open
    anyModalOpen() {
      return !!(
        this.ineligibleModalEvent
        || this.usawModalEvent
        || this.aauModalEvent
        || this.subscriptionPausedModalEvent
        || this.subscriptionOverdueModalEvent
        || this.weeklyLimitModalEvent
      );
    },
    formattedToday() {
      return moment().format('ddd, MMM D');
    },
    showGeneralCheckIn() {
      // Show general check-in option based on kiosk settings
      return this.allowGeneralCheckIn;
    },
    sortedEvents() {
      // Filter to only include events that:
      // 1. Are in the future OR started within the last 4 hours
      // 2. Are not the generic "General Check-in" event (id: -1)
      const cutoffTime = moment().subtract(4, 'hours');

      return this.events
        .filter((event) => {
          // Skip the generic check-in event (handled separately)
          if (event.id === -1) return false;

          // For all-day events, include them
          if (event.all_day) return true;

          // Check if event is still relevant (in future or started within 45 min)
          const eventStart = moment(event.start_at);
          return eventStart.isAfter(cutoffTime);
        })
        .sort((a, b) =>
          // Sort by start time
          moment(a.start_at).diff(moment(b.start_at)));
    },
    eligibleEvents() {
      return this.sortedEvents.filter((event) => this.canCheckIn(event));
    },
    ineligibleEvents() {
      return this.sortedEvents.filter((event) => !this.canCheckIn(event));
    },
    wrestlerMissingUsaw() {
      if (!this.requiresUsaw) return false;
      if (!this.checkInLogic) return false;
      return this.checkInLogic.missingUsawMembership(this.wrestler);
    },
    wrestlerMissingAau() {
      if (!this.requiresAau) return false;
      if (!this.checkInLogic) return false;
      return this.checkInLogic.missingAauMembership(this.wrestler);
    },
    rosterTags() {
      if (!this.wrestler?.rosters) return [];

      // Flatten all tags from all rosters, excluding archived tags
      const rosters = _.flatMap(this.wrestler.rosters);
      const tags = _.reject(_.flatMap(rosters, 'tags'), 'archived');

      // Count occurrences of each tag by abbreviation
      const count = _.countBy(tags, 'abbreviation');

      // Return unique tags with count
      return _.map(_.uniqBy(tags, 'abbreviation'), (t) => ({ ...t, count: count[t.abbreviation] }));
    },
    rosterNames() {
      if (!this.wrestler?.rosters) return '';

      // Filter to active rosters only, sort by name, join with commas
      const activeRosters = _.filter(this.wrestler.rosters, (r) => !r.archived);
      return _.map(_.sortBy(activeRosters, 'name'), 'name').join(', ');
    },
  },
  watch: {
    // Emit modal state changes for native app integration (hides camera tile)
    anyModalOpen(isOpen) {
      this.$emit('modal-state-changed', isOpen);
    },
  },
  mounted() {
    // Warp speed: auto-select when there's exactly one eligible event
    if (this.warpSpeed && this.eligibleEvents.length === 1) {
      this.$nextTick(() => {
        this.handleEventClick(this.eligibleEvents[0]);
      });
    }
  },
  methods: {
    canCheckIn(event) {
      // Check if wrestler is eligible for this event
      // If event has no roster restrictions, allow it
      if (!event.roster_events || event.roster_events.length === 0) {
        return true;
      }

      // Check if wrestler is in any of the event's rosters
      const wrestlerRosterIds = this.wrestler.rosters?.map((r) => r.id) || [];
      const eventRosterIds = event.roster_events.map((re) => re.roster.id);

      return wrestlerRosterIds.some((wrId) => eventRosterIds.includes(wrId));
    },
    handleEventClick(event) {
      // Subscription paused/overdue and the weekly class limit depend on which
      // event is picked (a camp registrant isn't held to their membership), so
      // the server decides those at check-in time and the parent routes its
      // 403 back through showCheckInBlockedModal.

      // Check for USAW requirement
      if (this.wrestlerMissingUsaw) {
        this.usawModalEvent = event;
        this.$emit('error-modal-opened', 'usaw');
        return;
      }

      // Check for AAU requirement
      if (this.wrestlerMissingAau) {
        this.aauModalEvent = event;
        this.$emit('error-modal-opened', 'aau');
        return;
      }

      // If passes all checks, proceed to select
      this.$emit('select', event);
    },
    handleForceCheckIn(event, type) {
      // Close all modals
      this.ineligibleModalEvent = null;
      this.usawModalEvent = null;
      this.aauModalEvent = null;
      this.subscriptionPausedModalEvent = null;
      this.subscriptionOverdueModalEvent = null;
      this.weeklyLimitModalEvent = null;
      // Emit force-checkin with event and type info
      this.$emit('force-checkin', { event, type });
    },
    // Opens the override modal for a server-side check-in block (a
    // CheckInEligibility code from the check-in POST's 403).
    showCheckInBlockedModal(event, code) {
      const modalFieldByCode = {
        subscription_paused: 'subscriptionPausedModalEvent',
        subscription_overdue: 'subscriptionOverdueModalEvent',
        weekly_limit_reached: 'weeklyLimitModalEvent',
      };
      const errorTypeByCode = {
        subscription_paused: 'subscription_paused',
        subscription_overdue: 'subscription_overdue',
        weekly_limit_reached: 'weekly_limit',
      };
      const field = modalFieldByCode[code];
      if (!field) return;

      this[field] = event;
      this.$emit('error-modal-opened', errorTypeByCode[code]);
    },
    showIneligibleInfo(event) {
      this.ineligibleModalEvent = event;
      this.$emit('error-modal-opened', 'roster');
    },
    formatEventTime(event) {
      if (!event || event.id === -1) return '';

      if (event.all_day) {
        return 'All day';
      }

      const start = moment(event.start_at);
      return start.format('h:mm A');
    },
  },
};
</script>
